// Tenant billing — PayFast checkout hand-off + the return / cancel landing screens.
// startPayfastCheckout() asks the API for the signed field set and POSTs the browser to PayFast
// (a full-page redirect). PayFast confirms server-to-server via the ITN webhook afterwards, so on
// return we poll /api/me until the subscription flips to active.
import { html, signal, navigate } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { logout } from '../shared/auth.js';
import { theme, toggleTheme } from '../shared/theme.js';
import { icon } from '../shared/icons.js';
import { bottomNav, tenantNavItems } from '../shared/bottomnav.js';
import { t, initI18n } from '../shared/i18n.js';

// Pure: which billing control a company row should show.
//  - active            → already paying (green pill)
//  - invite            → an accountant's business-pays client; the business activates, not us
//  - activate {price}  → offer the "Activate billing" button (business owner, or practice-pays)
export function billingRowState(c, isAccountant){
  if(c.sub_status === 'active') return { kind: 'active' };
  if(isAccountant && c.license_payer !== 'practice') return { kind: 'invite' };
  return { kind: 'activate', price: c.price_month || null };
}

// Ask the API to build the signed subscribe request, then POST it to PayFast as a full-page
// redirect. Resolves only on failure (on success the browser navigates away).
export async function startPayfastCheckout(companyId){
  const origin = location.origin;
  const data = await api.post(`/billing/companies/${companyId}/payfast/subscribe`, {
    return_url: `${origin}/billing/return`,
    cancel_url: `${origin}/billing/cancel`,
  });
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = data.process_url;
  Object.entries(data.fields || {}).forEach(([k, v]) => {
    const input = document.createElement('input');
    input.type = 'hidden'; input.name = k; input.value = String(v);
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

function shell(title, body){
  const signOut = () => { logout(); navigate('/login'); };
  return html`
    <div class="main tenant-main">
      <header class="app-bar">
        <div class="brand-lockup"><span class="brand-dot"></span><span class="brand-name">aboo</span></div>
        <div class="app-title">${title}</div>
        <div class="app-user">
          <button class="theme-toggle" title=${t('common.toggle_theme', 'Toggle light / dark')} @click=${() => toggleTheme()}>${() => theme.value === 'dark' ? icon('sun', {size: 18}) : icon('moon', {size: 18})}</button>
        </div>
      </header>
      <div class="content">${body}</div>
      ${bottomNav(tenantNavItems({ onSignOut: signOut }))}
    </div>`;
}

// Landing after PayFast redirects back. ITN is asynchronous, so poll /api/me a few times to see
// the subscription go active; if it hasn't within a few tries, reassure and let them carry on.
export function billingReturnScreen(){
  const status = signal('checking');   // checking | active | pending
  const importCompanyId = signal(null);
  let tries = 0;
  async function poll(){
    tries++;
    try {
      const me = await api.get('/me');
      const companies = me.companies || [];
      // The import offer needs a company to import INTO — prefer the one that just went active,
      // otherwise the only/first company on the account.
      const target = companies.find(c => c.sub_status === 'active') || companies[0];
      // Once we know the company, load its language bundle so this landing page translates
      // (the tenant shell's initI18n didn't run on this fresh post-redirect page load).
      if(target){ importCompanyId.value = target.id; initI18n(target.id); }
      if(companies.some(c => c.sub_status === 'active')){ status.value = 'active'; return; }
    } catch {}
    if(tries >= 5){ status.value = 'pending'; return; }
    setTimeout(poll, 2500);
  }
  poll();

  // The import entry point lives HERE, after billing — not in first-company onboarding. The
  // company and its seeded chart already exist by now, which is what the import maps onto. It is
  // also offered permanently on the company card in home.js, so it is never a one-shot users miss.
  // (importOffer is a reactive leaf keyed on importCompanyId; it also reads t() so it re-renders
  // when the bundle lands — plain t() inside, no nested ${() =>}, per the append footgun.)
  const importOffer = () => {
    const id = importCompanyId.value;
    if(!id) return null;
    return html`<div class="import-offer">
      <div class="form-note mb-2">${t('billing.import_offer_note', "Coming from Sage, Xero, QuickBooks or Pastel? Bring your chart of accounts across — we'll map it onto the one we've already set up for you, and show you the result before anything changes. You can also do this later from your home screen.")}</div>
      <button class="btn btn-outline" @click=${() => navigate('/companies/' + id + '/import')}>${t('billing.import_offer_button', 'Import my data')}</button>
    </div>`;
  };

  const body = () => html`<div class="onboard-wrap"><div class="login-card card onboard-card"><div class="card-body">
    ${() => status.value === 'active'
      ? html`<h2 class="onboard-title">${t('billing.return_active_title', "You're all set ✓")}</h2>
             <p class="login-hint mb-3">${t('billing.return_active_body', 'Your subscription is active — thanks for signing up.')}</p>`
      : status.value === 'pending'
        ? html`<h2 class="onboard-title">${t('billing.return_pending_title', 'Payment received')}</h2>
               <p class="login-hint mb-3">${t('billing.return_pending_body', "We're confirming it with PayFast — this can take a moment, and your billing will activate shortly.")}</p>`
        : html`<h2 class="onboard-title">${t('billing.return_checking_title', 'Finalising…')}</h2>
               <p class="login-hint mb-3">${t('billing.return_checking_body', 'Confirming your payment with PayFast.')}</p>`}
    ${importOffer}
    <div class="form-actions"><span class="form-actions-spacer"></span>
      <button class="btn btn-primary" @click=${() => navigate('/home')}>${() => t('common.back_home', 'Back to home')}</button></div>
  </div></div></div>`;
  return shell(() => t('common.billing', 'Billing'), body);
}

export function billingCancelScreen(){
  const body = html`<div class="onboard-wrap"><div class="login-card card onboard-card"><div class="card-body">
    <h2 class="onboard-title">${() => t('billing.cancel_title', 'Checkout cancelled')}</h2>
    <p class="login-hint mb-3">${() => t('billing.cancel_body', 'No problem — you can activate billing anytime from your home screen.')}</p>
    <div class="form-actions"><span class="form-actions-spacer"></span>
      <button class="btn btn-primary" @click=${() => navigate('/home')}>${() => t('common.back_home', 'Back to home')}</button></div>
  </div></div></div>`;
  return shell(() => t('common.billing', 'Billing'), body);
}
